import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../state/hooks";
import { closeHTMLDrawer } from "../../state/slices/htmlDrawerSlice";

export const HTMLDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, htmlContent, reportName, loading, error } = useAppSelector(
    (state) => state.htmlDrawer
  );
  const contentRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    dispatch(closeHTMLDrawer());
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when drawer is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // Inject HTML content into div
  useEffect(() => {
    if (isOpen && htmlContent && contentRef.current) {
      // Clear previous content
      contentRef.current.innerHTML = "";
      
      // Check if it's a full HTML document
      if (htmlContent.includes("<!DOCTYPE") || htmlContent.includes("<html")) {
        // For full HTML documents, we need to extract and properly structure the content
        // Create a temporary parser
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, "text/html");
        
        // Extract styles from head
        const headStyles = doc.querySelectorAll("head style");
        let styleContent = "";
        headStyles.forEach((style) => {
          styleContent += style.outerHTML;
        });
        
        // Extract body content
        const bodyContent = doc.body ? doc.body.innerHTML : htmlContent;
        
        // Combine styles and body content
        const contentToInject = styleContent + bodyContent;
        contentRef.current.innerHTML = contentToInject;
      } else {
        // For partial HTML, inject directly
        contentRef.current.innerHTML = htmlContent;
      }
    }
  }, [isOpen, htmlContent]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[10000] bg-black bg-opacity-70 transition-opacity duration-300"
        onClick={handleClose}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div
        className="fixed right-0 top-0 bottom-0 z-[10001] w-full max-w-5xl bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out flex flex-col"
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 flex-shrink-0">
          <div className="flex items-center gap-3">
            {/* <svg
              className="w-6 h-6 text-primary-600 dark:text-primary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg> */}
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
             { ""}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors"
            aria-label="Close drawer"
          >
            <svg
              className="w-6 h-6 text-gray-600 dark:text-gray-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden relative">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800">
              <div className="flex flex-col items-center gap-4">
                <div className="w-12 h-12 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
                <p className="text-gray-600 dark:text-gray-300">Loading content...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800 p-6">
              <div className="text-center">
                <svg
                  className="w-16 h-16 text-red-500 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                  Error Loading Content
                </p>
                <p className="text-gray-600 dark:text-gray-400">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && htmlContent && (
            <div
              ref={contentRef}
              className="html-drawer-content-wrapper w-full h-full overflow-auto"
              style={{
                // Ensure content fits properly and displays exact HTML
                boxSizing: "border-box",
                // Always use white background for reports (designed for light backgrounds)
                // This ensures readability in both light and dark mode
                backgroundColor: "#ffffff",
                padding: "20px",
                // Ensure proper isolation for the HTML content
                isolation: "isolate",
                // Force light mode colors - prevent dark mode from affecting report content
                colorScheme: "light",
              }}
            />
          )}

          {!loading && !error && !htmlContent && (
            <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-800">
              <p className="text-gray-500 dark:text-gray-400">No content to display</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
